import { useState } from 'react';
import { Button, Icon, Modal, Typography } from '@/primer/components';
import type { TaskRejectionModalCopy } from '@/utils/taskRejectionReasons';

interface PostVerificationFeedbackProps {
  reason: TaskRejectionModalCopy;
  canSubmit: boolean;
  isPending: boolean;
  onVerify: () => void;
}

const FAILURE_REASONS = [
  "Post isn't public.",
  'Required mentions are missing.',
  "Branded Content wasn't enabled.",
  "The post hasn't propagated yet.",
];

/** Rejected-task footer and its specific and generic help dialogs. */
export function PostVerificationFeedback({
  reason,
  canSubmit,
  isPending,
  onVerify,
}: PostVerificationFeedbackProps) {
  const [dialog, setDialog] = useState<'rejection' | 'reasons'>('rejection');
  const [isOpen, setIsOpen] = useState(true);
  const close = () => setIsOpen(false);
  const title = dialog === 'reasons' ? 'Possible reasons for failure' : reason.title;

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-center">
          <div className="flex items-center gap-1" role="alert">
            <Icon name="alert" size="small" color="warning" />
            <Typography variant="body" size="small" color="bodylight">
              We couldn’t verify your post.
            </Typography>
          </div>
          <Button
            variant="tertiary"
            size="small"
            onClick={() => {
              setDialog('reasons');
              setIsOpen(true);
            }}
          >
            Learn more
          </Button>
        </div>
        <Button
          variant="primary"
          size="large"
          fullWidth
          onClick={onVerify}
          disabled={!canSubmit || isPending}
          isLoading={isPending}
        >
          Verify again
        </Button>
      </div>
      <Modal
        isOpen={isOpen}
        onClose={close}
        ariaLabel={title}
        primaryButton={{ label: 'Got it', onClick: close }}
      >
        <div className="flex flex-col gap-3">
          <div className="flex justify-center">
            <div className="flex items-center justify-center size-10 rounded-full bg-interactive-quiet">
              <Icon name="circle-info-filled" size="large" color="interactive" />
            </div>
          </div>
          <div className="text-center">
            <Typography variant="header" size="h2" as="h2">
              {title}
            </Typography>
          </div>
          {dialog === 'reasons' ? (
            <ul className="flex flex-col gap-2 list-disc pl-5">
              {FAILURE_REASONS.map((failure) => (
                <li key={failure}>
                  <Typography variant="body" size="medium" as="span">
                    {failure}
                  </Typography>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center">
              <Typography variant="body" size="medium">
                {reason.description}
              </Typography>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
